import { useState, useEffect } from "react";
import axios from "axios";
import { X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoGalleryManager = ({ galleries, onRefresh, token }) => {
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [newGallery, setNewGallery] = useState({ title: "", description: "", date: "" });
  const [showNewGallery, setShowNewGallery] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [photoDescriptions, setPhotoDescriptions] = useState({});

  useEffect(() => {
    if (selectedGallery) {
      fetchGalleryPhotos(selectedGallery.id);
    }
  }, [selectedGallery]);

  const fetchGalleryPhotos = async (galleryId) => {
    try {
      const res = await axios.get(`${API}/galleries/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error("Error fetching photos:", error);
    }
  };

  const handleCreateGallery = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/galleries`, newGallery, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewGallery({ title: "", description: "", date: "" });
      setShowNewGallery(false);
      onRefresh();
    } catch (error) {
      console.error("Error creating gallery:", error);
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    if (!window.confirm("Delete this gallery and all its photos?")) return;
    try {
      await axios.delete(`${API}/galleries/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedGallery(null);
      onRefresh();
    } catch (error) {
      console.error("Error deleting gallery:", error);
    }
  };

  const handleUploadPhotos = async (e) => {
    const files = e.target.files;
    if (!files || !selectedGallery) return;

    setIsUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("description", "");
      formData.append("cutline", "");

      try {
        await axios.post(`${API}/galleries/${selectedGallery.id}/photos`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } catch (error) {
        console.error("Error uploading photo:", error);
      }
    }
    setIsUploading(false);
    fetchGalleryPhotos(selectedGallery.id);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchGalleryPhotos(selectedGallery.id);
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  const handleUpdatePhoto = async (photoId) => {
    try {
      await axios.put(`${API}/photos/${photoId}`, null, {
        params: {
          description: photoDescriptions[photoId]?.description || "",
          cutline: photoDescriptions[photoId]?.cutline || "",
        },
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error updating photo:", error);
    }
  };

  const getPhotoUrl = (photo, thumbnail = true) => {
    const path = thumbnail ? photo.thumbnail_path : photo.storage_path;
    return `${API}/files/${path}`;
  };

  return (
    <div className="space-y-6">
      {/* Gallery List */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Photo Galleries</h3>
        <button
          onClick={() => setShowNewGallery(true)}
          className="btn-primary text-sm"
          data-testid="create-gallery-btn"
        >
          New Gallery
        </button>
      </div>

      {/* New Gallery Form */}
      {showNewGallery && (
        <form onSubmit={handleCreateGallery} className="glass-card space-y-4">
          <div>
            <label className="form-label">Title</label>
            <input
              type="text"
              value={newGallery.title}
              onChange={(e) => setNewGallery({ ...newGallery, title: e.target.value })}
              required
              className="form-input"
              data-testid="gallery-title-input"
            />
          </div>
          <div>
            <label className="form-label">Description</label>
            <textarea
              value={newGallery.description}
              onChange={(e) => setNewGallery({ ...newGallery, description: e.target.value })}
              className="form-input"
              rows={2}
            />
          </div>
          <div>
            <label className="form-label">Date</label>
            <input
              type="date"
              value={newGallery.date}
              onChange={(e) => setNewGallery({ ...newGallery, date: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" data-testid="save-gallery-btn">
              Create Gallery
            </button>
            <button
              type="button"
              onClick={() => setShowNewGallery(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Gallery Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {galleries.map((gallery) => (
          <div
            key={gallery.id}
            onClick={() => setSelectedGallery(gallery)}
            className={`glass-card cursor-pointer transition-all hover:border-its-gold/50 ${
              selectedGallery?.id === gallery.id ? "border-its-gold ring-2 ring-its-gold/20" : ""
            }`}
          >
            <h4 className="font-semibold text-white mb-1">{gallery.title}</h4>
            <p className="text-xs text-its-offwhite/60 mb-2">{gallery.date}</p>
            <p className="text-sm text-its-offwhite/80 line-clamp-2">{gallery.description}</p>
          </div>
        ))}
      </div>

      {/* Selected Gallery Photos */}
      {selectedGallery && (
        <div className="glass-card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white">{selectedGallery.title} - Photos</h4>
            <div className="flex gap-2">
              <label className="btn-primary text-sm cursor-pointer">
                {isUploading ? "Uploading..." : "Upload Photos"}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleUploadPhotos}
                  className="hidden"
                  disabled={isUploading}
                  data-testid="photo-upload-input"
                />
              </label>
              <button
                onClick={() => handleDeleteGallery(selectedGallery.id)}
                className="px-4 py-2 text-sm bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/30 transition-colors"
              >
                Delete Gallery
              </button>
            </div>
          </div>

          {photos.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-8">No photos yet. Upload some!</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={getPhotoUrl(photo, true)}
                    alt={photo.description || "Gallery photo"}
                    className="w-full h-32 object-cover rounded-lg"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button
                      onClick={() => setLightboxPhoto(photo)}
                      className="p-2 bg-its-gold/80 rounded-full text-its-teal hover:bg-its-gold"
                    >
                      <ZoomIn size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-500"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
        <DialogContent className="max-w-4xl bg-its-teal-dark border-its-teal-light/20">
          <DialogHeader>
            <DialogTitle className="text-white">
              {lightboxPhoto?.description || "Photo Preview"}
            </DialogTitle>
          </DialogHeader>
          {lightboxPhoto && (
            <div className="space-y-4">
              <img
                src={getPhotoUrl(lightboxPhoto, false)}
                alt={lightboxPhoto.description || "Full size photo"}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    value={photoDescriptions[lightboxPhoto.id]?.description ?? lightboxPhoto.description}
                    onChange={(e) =>
                      setPhotoDescriptions({
                        ...photoDescriptions,
                        [lightboxPhoto.id]: {
                          ...photoDescriptions[lightboxPhoto.id],
                          description: e.target.value,
                        },
                      })
                    }
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Cutline</label>
                  <input
                    type="text"
                    value={photoDescriptions[lightboxPhoto.id]?.cutline ?? lightboxPhoto.cutline}
                    onChange={(e) =>
                      setPhotoDescriptions({
                        ...photoDescriptions,
                        [lightboxPhoto.id]: {
                          ...photoDescriptions[lightboxPhoto.id],
                          cutline: e.target.value,
                        },
                      })
                    }
                    className="form-input"
                  />
                </div>
              </div>
              <button
                onClick={() => handleUpdatePhoto(lightboxPhoto.id)}
                className="btn-primary"
              >
                Save Details
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
